import React, { useState, useEffect } from 'react';
import {
 PlusIcon,
 PencilIcon,
 TrashIcon,
 ArrowUpIcon,
 ArrowDownIcon,
 CheckCircleIcon,
 XCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = (token) => ({
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
});

const AdditionalAdmissionQuestions = () => {
 const { token } = useAuth();
 const { showSuccess, showError } = useToast();
 const [questions, setQuestions] = useState([]);
 const [loading, setLoading] = useState(true);
 const [showModal, setShowModal] = useState(false);
 const [editingQuestion, setEditingQuestion] = useState(null);
 const [formData, setFormData] = useState({
 question_label: '',
 question_type: 'text',
 options: [''],
 is_required: false
 });

 const questionTypes = [
 { value: 'text', label: 'Text' },
 { value: 'number', label: 'Number' },
 { value: 'dropdown', label: 'Dropdown' },
 { value: 'yes_no', label: 'Yes/No' }
 ];

 useEffect(() => {
 fetchQuestions();
 }, []);

 const fetchQuestions = async () => {
 try {
 setLoading(true);
 const response = await fetch(`${API_BASE_URL}/university/admission-questions`, {
 headers: getAuthHeaders(token)
 });

 if (response.ok) {
 const data = await response.json();
 setQuestions(data.questions || []);
 } else if (response.status === 403) {
 showError('This feature is only available for partner universities');
 } else {
 showError('Failed to load questions');
 }
 } catch (error) {
 console.error('Error fetching questions:', error);
 showError('Error loading questions');
 } finally {
 setLoading(false);
 }
 };

 const handleOpenModal = (question = null) => {
 if (question) {
 setEditingQuestion(question);
 setFormData({
 question_label: question.question_label,
 question_type: question.question_type,
 options: question.options.length > 0 ? question.options : [''],
 is_required: question.is_required
 });
 } else {
 setEditingQuestion(null);
 setFormData({
 question_label: '',
 question_type: 'text',
 options: [''],
 is_required: false
 });
 }
 setShowModal(true);
 };

 const handleCloseModal = () => {
 setShowModal(false);
 setEditingQuestion(null);
 setFormData({
 question_label: '',
 question_type: 'text',
 options: [''],
 is_required: false
 });
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!formData.question_label.trim()) {
 showError('Question label is required');
 return;
 }

 if (formData.question_type === 'dropdown' && formData.options.filter(o => o.trim()).length === 0) {
 showError('Dropdown questions must have at least one option');
 return;
 }

 try {
 const url = editingQuestion
 ? `${API_BASE_URL}/university/admission-questions/${editingQuestion.id}`
 : `${API_BASE_URL}/university/admission-questions`;

 const method = editingQuestion ? 'PUT' : 'POST';

 const payload = {
 question_label: formData.question_label,
 question_type: formData.question_type,
 is_required: formData.is_required,
 options: formData.question_type === 'dropdown' ? formData.options.filter(o => o.trim()) : []
 };

 const response = await fetch(url, {
 method,
 headers: getAuthHeaders(token),
 body: JSON.stringify(payload)
 });

 if (response.ok) {
 showSuccess(editingQuestion ? 'Question updated successfully' : 'Question created successfully');
 handleCloseModal();
 fetchQuestions();
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to save question');
 }
 } catch (error) {
 console.error('Error saving question:', error);
 showError('Error saving question');
 }
 };

 const handleDelete = async (questionId) => {
 if (!window.confirm('Are you sure you want to delete this question?')) {
 return;
 }

 try {
 const response = await fetch(`${API_BASE_URL}/university/admission-questions/${questionId}`, {
 method: 'DELETE',
 headers: getAuthHeaders(token)
 });

 if (response.ok) {
 showSuccess('Question deleted successfully');
 fetchQuestions();
 } else {
 showError('Failed to delete question');
 }
 } catch (error) {
 console.error('Error deleting question:', error);
 showError('Error deleting question');
 }
 };

 const handleMoveQuestion = async (index, direction) => {
 const newQuestions = [...questions];
 const targetIndex = direction === 'up' ? index - 1 : index + 1;

 if (targetIndex < 0 || targetIndex >= newQuestions.length) {
 return;
 }

 // Swap
 [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];

 // Update display_order
 const updates = newQuestions.map((q, idx) => ({
 id: q.id,
 display_order: idx
 }));

 try {
 const response = await fetch(`${API_BASE_URL}/university/admission-questions/reorder`, {
 method: 'PUT',
 headers: getAuthHeaders(token),
 body: JSON.stringify({ questions: updates })
 });

 if (response.ok) {
 setQuestions(newQuestions);
 } else {
 showError('Failed to reorder questions');
 }
 } catch (error) {
 console.error('Error reordering questions:', error);
 showError('Error reordering questions');
 }
 };

 const addOption = () => {
 setFormData({
 ...formData,
 options: [...formData.options, '']
 });
 };

 const updateOption = (index, value) => {
 const newOptions = [...formData.options];
 newOptions[index] = value;
 setFormData({ ...formData, options: newOptions });
 };

 const removeOption = (index) => {
 const newOptions = formData.options.filter((_, i) => i !== index);
 setFormData({ ...formData, options: newOptions });
 };

 return (
 <div className="space-y-6">
 <div className="card relative overflow-hidden">
 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
 <div>
 <h3 className="text-lg font-bold text-slate-800">Additional Admission Information</h3>
 <p className="text-sm text-slate-500 mt-1">
 Create custom questions for applicants to answer during the application process
 </p>
 </div>
 <button
 onClick={() => handleOpenModal()}
 className="btn-primary inline-flex items-center text-sm w-full sm:w-auto justify-center"
 >
 <PlusIcon className="h-4 w-4 mr-2" />
 <span>Add Question</span>
 </button>
 </div>

 {loading ? (
 <div className="text-center py-12">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
 <p className="text-slate-500 mt-4 text-sm font-semibold">Loading questions...</p>
 </div>
 ) : questions.length === 0 ? (
 <div className="text-center py-12 text-slate-400 bg-slate-50 border border-slate-100 rounded-2xl">
 <PlusIcon className="h-16 w-16 mx-auto mb-4 text-slate-300" />
 <p className="font-bold text-slate-700 text-lg">No Questions Yet</p>
 <p className="text-sm mt-1 max-w-sm mx-auto font-medium">Click "Add Question" to create your first custom question.</p>
 </div>
 ) : (
 <div className="space-y-4">
 {questions.map((question, index) => (
 <div key={question.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-primary-100 hover:shadow-sm transition-all duration-300">
 <div className="flex items-start justify-between">
 <div className="flex-1 min-w-0">
 <div className="flex flex-wrap items-center gap-2 mb-2">
 <span className="text-sm font-bold text-slate-800">
 Q{index + 1}:
 </span>
 <span className="text-sm text-slate-700 font-medium break-words max-w-xl">{question.question_label}</span>
 {question.is_required && (
 <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-rose-50 border border-rose-200/50 text-rose-700 uppercase tracking-wider">
 Required
 </span>
 )}
 </div>
 <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 font-medium">
 <span className="capitalize">Type: {question.question_type.replace('_', ' ')}</span>
 {question.question_type === 'dropdown' && question.options.length > 0 && (
 <span className="truncate">Options: {question.options.join(', ')}</span>
 )}
 </div>
 </div>
 <div className="flex items-center space-x-1 sm:space-x-2 ml-4 shrink-0">
 <button
 onClick={() => handleMoveQuestion(index, 'up')}
 disabled={index === 0}
 className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl transition-all"
 title="Move up"
 >
 <ArrowUpIcon className="h-4.5 w-4.5" />
 </button>
 <button
 onClick={() => handleMoveQuestion(index, 'down')}
 disabled={index === questions.length - 1}
 className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl transition-all"
 title="Move down"
 >
 <ArrowDownIcon className="h-4.5 w-4.5" />
 </button>
 <button
 onClick={() => handleOpenModal(question)}
 className="text-primary-600 hover:text-primary-700 p-2 rounded-xl hover:bg-primary-50 transition-all active:scale-95 border border-slate-100"
 title="Edit"
 >
 <PencilIcon className="h-4.5 w-4.5" />
 </button>
 <button
 onClick={() => handleDelete(question.id)}
 className="text-red-500 hover:text-red-700 p-2.5 rounded-xl hover:bg-red-50 transition-all active:scale-95 border border-slate-100"
 title="Delete"
 >
 <TrashIcon className="h-4.5 w-4.5" />
 </button>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>

 {/* Modal */}
 {showModal && (
 <div className="fixed inset-0 bg-slate-900/60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 animate-fade-in">
 <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 shadow-2xl relative p-6">
 
 <div className="flex justify-between items-center mb-6">
 <h3 className="text-lg font-bold text-slate-800">
 {editingQuestion ? 'Edit Question' : 'Add New Question'}
 </h3>
 <button
 onClick={handleCloseModal}
 className="text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
 >
 <XCircleIcon className="h-6 w-6" />
 </button>
 </div>

 <form onSubmit={handleSubmit} className="space-y-4">
 {/* Question Label */}
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
 Question <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 value={formData.question_label}
 onChange={(e) => setFormData({ ...formData, question_label: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 placeholder="Enter your question"
 required
 />
 </div>

 {/* Question Type */}
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
 Question Type <span className="text-red-500">*</span>
 </label>
 <select
 value={formData.question_type}
 onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300 font-medium cursor-pointer"
 >
 {questionTypes.map((type) => (
 <option key={type.value} value={type.value}>
 {type.label}
 </option>
 ))}
 </select>
 </div>

 {/* Dropdown Options */}
 {formData.question_type === 'dropdown' && (
 <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
 Options <span className="text-red-500">*</span>
 </label>
 <div className="space-y-2">
 {formData.options.map((option, index) => (
 <div key={index} className="flex items-center space-x-2">
 <input
 type="text"
 value={option}
 onChange={(e) => updateOption(index, e.target.value)}
 className="flex-1 border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 bg-white text-slate-700"
 placeholder={`Option ${index + 1}`}
 />
 {formData.options.length > 1 && (
 <button
 type="button"
 onClick={() => removeOption(index)}
 className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-xl transition-all active:scale-95"
 >
 <TrashIcon className="h-4.5 w-4.5" />
 </button>
 )}
 </div>
 ))}
 <button
 type="button"
 onClick={addOption}
 className="inline-flex items-center text-sm font-semibold text-primary-600 hover:text-primary-700 mt-2 hover:underline"
 >
 + Add Option
 </button>
 </div>
 </div>
 )}

 {/* Is Required */}
 <div className="flex items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
 <input
 type="checkbox"
 id="is_required"
 checked={formData.is_required}
 onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
 className="h-4 w-4 text-primary-600 focus:ring-primary-500/20 border-slate-200 rounded cursor-pointer transition-colors"
 />
 <label htmlFor="is_required" className="ml-2 block text-sm font-semibold text-slate-700 cursor-pointer">
 Make this question required
 </label>
 </div>

 {/* Actions */}
 <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-6">
 <button
 type="button"
 onClick={handleCloseModal}
 className="btn-secondary py-2 px-5 text-sm"
 >
 Cancel
 </button>
 <button
 type="submit"
 className="btn-primary py-2 px-5 text-sm"
 >
 {editingQuestion ? 'Update Question' : 'Create Question'}
 </button>
 </div>
 </form>
 </div>
 </div>
 )}
 </div>
 );
};

export default AdditionalAdmissionQuestions;
